<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';

import { AlgoliaVariant, browseProducts } from '@/api/algolia';
import { reportEngagement } from '@/api/dynamic-yield/engagement';
import Checkbox from '@/components/base/form/Checkbox.vue';
import HorizontalProductCard from '@/components/base/layout/HorizontalProductCard.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { SpacingDirection, SpacingLevel, useSpacing } from '@/composables/useSpacing';
import money from '@/filters/money';
import { AnalyticsMetadata, BaseChoice } from '@/lib/personalization/dynamicYield';
import {
  formatAnalyticsMetadata,
  ListSourceAttribution,
} from '@/utils/analytics/listSourceAttribution';
import {
  formatNutsAddToCartItemFromProductCard,
  sendProductAddedEvent,
} from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import { Money } from '@/utils/money';
import { displayPrice, ProductCard, ProductCardData } from '@/utils/productCard';

const props = withDefaults(
  defineProps<{
    analyticsMetadata?: AnalyticsMetadata;
    currentProductKey: string;
    decisionId?: BaseChoice['decisionId'];
    recommendations: ProductCardData[];
    spacingDirection?: SpacingDirection;
    spacingLevel?: SpacingLevel;
    selectedVariantSku: string;
    title?: string;
  }>(),
  {
    title: 'Frequently bought together',
    dataTest: 'recommendations-raw-title',
  },
);

const store = useStore();
const { addToCart, cart } = useCart(store);
const { spacing } = useSpacing(props);

const variants = ref<AlgoliaVariant[]>();
const firstItem = computed(() => {
  const item = variants.value?.find((variant) => variant.sku === props.selectedVariantSku);
  return item ? ProductCard.fromAlgolia(item) : undefined;
});

const availableRecommendations = computed(() =>
  props.recommendations.filter((rec) => !rec.requiresCustomization),
);
const recommendationsToShow = computed(() => {
  if (firstItem.value) {
    return [firstItem.value, ...availableRecommendations.value.slice(0, 3)];
  }
  return availableRecommendations.value.slice(0, 4);
});

const checkedProducts = ref<ProductCardData[]>([]);

const handleCheckbox = (checked: boolean, product: ProductCardData) => {
  if (checked) checkedProducts.value.push(product);
  else checkedProducts.value = checkedProducts.value.filter((item) => item.sku !== product.sku);
};

const listSourceAttribution = computed<ListSourceAttribution | undefined>(
  () =>
    props.analyticsMetadata && {
      ...formatAnalyticsMetadata(props.analyticsMetadata),
      listSource: 'Dynamic Yield',
      listTitle: props.title,
    },
);

const totalPrice = computed(() =>
  Money.sumBy(checkedProducts.value, (product) => product.piecePrice),
);

const uniqueId = computed(() => {
  const title = props.title?.toLowerCase().replace(/ /g, '-') || 'title';
  return `bundled-recommendations-${title}`;
});

const addAllToCart = useCallback(async () => {
  await Promise.all(
    checkedProducts.value.map((product, index) => {
      sendProductAddedEvent(
        formatNutsAddToCartItemFromProductCard(product, {
          cartId: getCartId(cart.value),
          markedAsGift: false,
          itemListName: 'Bundled Recommendations Products',
          listSourceAttribution: listSourceAttribution.value,
          pageSection: 'Product Recommendations',
          quantity: 1,
        }),
      );

      if (props.decisionId && product.slotId) {
        reportEngagement(props.decisionId, product.slotId);
      }

      return addToCart(
        {
          cart_sku: {
            sku_external_id: product.sku,
            quantity: 1,
          },
        },
        {
          isComplementaryItem: index > 0,
          postAddToCartCallback: () => null,
        },
      );
    }),
  );
});

watchEffect(() => {
  checkedProducts.value = recommendationsToShow.value;
});

const loadVariants = async () => {
  variants.value = await browseProducts({
    query: props.currentProductKey,
    facetFilters: ['shortVariantName:-sample', 'shortVariantName:-mini box'],
  });
};

onMounted(() => {
  watchEffect(async () => {
    if (props.currentProductKey) await loadVariants();
  });
});
</script>

<template>
  <fieldset :aria-labelledby="uniqueId" :class="spacing" data-test="recommendations-section">
    <div class="flex justify-between">
      <Header4 :id="uniqueId">{{ title }}</Header4>
      <ThemedButton
        class="hidden w-full mt-3 lg:mt-0 lg:w-1/4 lg:visible lg:block"
        :disabled="checkedProducts.length === 0"
        :isLoading="addAllToCart.isPending"
        data-test="bundled-recos-add-all-to-cart-button"
        @click="addAllToCart.execute"
      >
        Add All to Cart - {{ money(totalPrice) }}
      </ThemedButton>
    </div>
    <div
      class="p-3 mt-5 bg-white border-solid border-neutral-300 rounded-2xl"
      data-test="recommendations-raw-products"
    >
      <ul
        class="grid items-center justify-between grid-cols-1 divide-y lg:grid-cols-4 lg:divide-y-0 divide-solid lg:divide-x"
      >
        <li
          v-for="(product, index) in recommendationsToShow"
          :key="product.sku"
          class="flex items-center w-full h-full px-4 py-3 border-solid lg:py-2 border-neutral-300"
        >
          <Checkbox
            class="mr-2"
            :id="`product-${index}-checkbox`"
            :modelValue="checkedProducts.some((p) => p.sku === product.sku)"
            @update:modelValue="handleCheckbox($event, product)"
          />
          <HorizontalProductCard
            :decisionId
            imageSize="small"
            :index
            :linkToPDP="!(product.sku === selectedVariantSku)"
            :listSourceAttribution
            pageSection="Product Recommendations"
            :product
          >
            <template v-if="product.sku === selectedVariantSku" v-slot:title>
              This item: {{ product.name }}
            </template>
            <template v-slot:price>
              <span v-if="product.totalSavings" class="sr-only">Price reduced from </span>
              <BaseBodyText
                :class="product.totalSavings?.onSale ? 'text-nuts-red-800' : 'text-neutral-500'"
              >
                {{ product.hidePrice ? 'Build your own' : displayPrice(product) }}
              </BaseBodyText>
              <span v-if="product.totalSavings" class="sr-only"> to </span>
              <SmallBodyText
                v-if="product.totalSavings"
                class="ml-1.5 line-through text-neutral-500"
              >
                {{ money(product.totalSavings.comparisonPrice) }}
              </SmallBodyText>
            </template>
          </HorizontalProductCard>
        </li>
      </ul>
      <ThemedButton
        class="w-full mt-3 lg:mt-0 lg:w-1/4 lg:hidden"
        :disabled="checkedProducts.length === 0"
        :isLoading="addAllToCart.isPending"
        data-test="bundled-recos-add-all-to-cart-button"
        @click="addAllToCart.execute"
      >
        Add All to Cart - {{ money(totalPrice) }}
      </ThemedButton>
    </div>
  </fieldset>
</template>
